import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { api } from '../services/api.ts';
import { formatDate, normalizeDate, formatCurrency } from '../utils/dateUtils.ts';
import { notificationService } from '../services/notificationService.ts';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext.tsx';
import { maskCurrency } from '../utils/balanceMask.ts';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  ChartDataLabels
);

interface Account {
  id: number;
  name: string;
  balance: number;
  is_investment?: boolean;
  server_id?: number; // Para compatibilidade com IndexedDB
}

interface Category {
  id: number;
  name: string;
  type: 'income' | 'expense';
  icon?: string;
  icon_color?: string;
  server_id?: number; // Para compatibilidade com IndexedDB
}

interface Transaction {
  id: number;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  due_date: string;
  is_paid: boolean;
  account_id?: number;
  category_id?: number;
  recurring_transaction_id?: number;
  is_recurring?: boolean;
  installment_number?: number;
  account: {
    name: string;
  };
  category: {
    name: string;
    icon?: string;
    icon_color?: string;
    color?: string; // Para compatibilidade com backend
  };
}

interface Task {
  id: number;
  title: string;
  description: string;
  due_date: string;
  is_urgent: boolean;
  is_important: boolean;
  status: 'pending' | 'completed';
  transaction_id?: number;
}

interface OkrIndicator {
  id: number;
  month: string;
}

interface OkrKeyResult {
  id: number;
  title: string;
}

interface OkrObjective {
  id: number;
  title: string;
  period_type: 'annual' | 'quarterly';
  period_start: string;
  period_end: string;
  key_results: OkrKeyResult[];
}

interface DashboardData {
  totalBalance: number;
  totalIncome: number;
  totalExpenses: number;
  pendingTransactions: number;
  upcomingTransactions: Transaction[];
  accounts: Account[];
  upcomingTasks: Task[];
  missingOkrIndicators: OkrObjective[];
  expensesByCategory: { category: string; amount: number; color: string }[];
  futureProjections: { month: string; income: number; expenses: number }[];
}

// Paleta de cores predefinida para garantir variedade
const COLOR_PALETTE = [
  '#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe',
  '#43e97b', '#fa709a', '#fee140', '#30cfd0', '#a8edea',
  '#fed6e3', '#ffecd2', '#fcb69f', '#ff9a9e', '#fecfef',
  '#fad0c4', '#ffd1ff', '#a1c4fd', '#c2e9fb', '#ffecd2',
  '#ef4444', '#10b981', '#f59e0b', '#3b82f6', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#a855f7'
];

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { isVisible } = useBalanceVisibility();
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    totalBalance: 0,
    totalIncome: 0,
    totalExpenses: 0,
    pendingTransactions: 0,
    upcomingTransactions: [],
    accounts: [],
    upcomingTasks: [],
    missingOkrIndicators: [],
    expensesByCategory: [],
    futureProjections: []
  });
  const [loading, setLoading] = useState(true);

  // Notificações de vencimentos
  useEffect(() => {
    const checkAndNotifyUpcoming = async () => {
      if (dashboardData.upcomingTransactions.length > 0) {
        // Filtrar apenas transações não pagas
        const unpaidTransactions = dashboardData.upcomingTransactions.filter(
          (t) => !t.is_paid
        );
        
        if (unpaidTransactions.length > 0) {
          await notificationService.notifyUpcomingDue(unpaidTransactions);
        }
      }
    };

    // Verificar vencimentos quando os dados do dashboard forem atualizados
    if (dashboardData.upcomingTransactions.length > 0) {
      checkAndNotifyUpcoming();
    }
  }, [dashboardData.upcomingTransactions]);

  const handleMarkAsPaid = async (id: number) => {
    try {
      await api.put(`/api/v1/transactions/${id}/mark-paid`);
      
      // Encontrar a transação para notificar
      const transaction = dashboardData.upcomingTransactions.find(t => t.id === id);
      if (transaction) {
        await notificationService.notifyTransactionPaid(transaction);
      }
      
      fetchDashboardData(); // Recarregar dados do dashboard
    } catch (error) {
      console.error('Erro ao marcar transação como paga:', error);
    }
  };

  const handleMarkAsUnpaid = async (id: number) => {
    try {
      await api.put(`/api/v1/transactions/${id}/mark-unpaid`);
      fetchDashboardData(); // Recarregar dados do dashboard
    } catch (error) {
      console.error('Erro ao desfazer transação paga:', error);
    }
  };

  // Função para enriquecer transações com dados de category e account
  const enrichTransactions = useCallback((transactionsData: any[], categoriesList: Category[], accountsList: Account[]): Transaction[] => {
    return transactionsData.map((t: any) => {
      // Se já tem category e account populados (vindo do backend), retorna como está
      if (t.category && t.account) {
        return t as Transaction;
      }
      
      // Caso contrário, busca nos arrays locais de categories e accounts
      const category = categoriesList.find(c => 
        c.id === t.category_id || 
        c.server_id === t.category_id ||
        t.category_id === c.server_id
      );
      const account = accountsList.find(a => 
        a.id === t.account_id || 
        a.server_id === t.account_id ||
        t.account_id === a.server_id
      );
      
      return {
        ...t,
        category: category ? {
          name: category.name,
          icon: category.icon || '📋',
          icon_color: category.icon_color || category.color || '#666666',
          color: category.icon_color || category.color || '#666666'
        } : {
          name: 'Categoria não encontrada',
          icon: '📋',
          icon_color: '#666666',
          color: '#666666'
        },
        account: account ? {
          name: account.name
        } : {
          name: 'Conta não encontrada'
        }
      } as Transaction;
    });
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [accountsRes, categoriesRes, transactionsRes, objectivesRes] = await Promise.all([
        api.get('/api/v1/accounts'),
        api.get('/api/v1/categories'),
        api.get('/api/v1/transactions'),
        api.get('/api/v1/objectives')
      ]);

      const accounts = accountsRes.data || [];
      const categoriesData = categoriesRes.data || [];
      const transactions = transactionsRes.data || [];
      const objectives = objectivesRes.data || [];
      
      // Enriquecer transações com dados de category e account
      const enrichedTransactions = enrichTransactions(transactions, categoriesData, accounts);

      // Calcular totais baseado em transações pagas
      const paidTransactions = enrichedTransactions.filter((t: Transaction) => t.is_paid);
      const totalIncome = paidTransactions
        .filter((t: Transaction) => t.type === 'income')
        .reduce((sum: number, t: Transaction) => sum + t.amount, 0);
      
      const totalExpenses = paidTransactions
        .filter((t: Transaction) => t.type === 'expense')
        .reduce((sum: number, t: Transaction) => sum + t.amount, 0);
      
      // Caixa: soma do saldo das contas que NÃO são investimento
      // (usa o balance do backend, que reflete saldo inicial e o efeito de transações pagas, incluindo transferências)
      const totalBalance = (accounts as Account[])
        .filter((a) => !a.is_investment)
        .reduce((sum, a) => sum + (Number.isFinite(a.balance) ? a.balance : 0), 0);

      // Transações vencidas ou que vencem em até 5 dias
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Zerar horas para comparação apenas de data
      const fiveDaysFromNow = new Date();
      fiveDaysFromNow.setDate(today.getDate() + 5);
      fiveDaysFromNow.setHours(23, 59, 59, 999); // Final do dia
      
      const upcomingTransactions = enrichedTransactions
        .filter((t: Transaction) => {
          // Normalizar data para evitar problemas de timezone
          const normalizedDueDate = normalizeDate(t.due_date);
          const [year, month, day] = normalizedDueDate.split('-');
          const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          dueDate.setHours(0, 0, 0, 0); // Zerar horas para comparação apenas de data
          return dueDate <= fiveDaysFromNow && !t.is_paid; // Inclui vencidas e próximas, mas apenas não pagas
        })
        .sort((a: Transaction, b: Transaction) => {
          // Usar normalizeDate para ordenação também
          const normalizedA = normalizeDate(a.due_date);
          const normalizedB = normalizeDate(b.due_date);
          const [yearA, monthA, dayA] = normalizedA.split('-');
          const [yearB, monthB, dayB] = normalizedB.split('-');
          const dateA = new Date(parseInt(yearA), parseInt(monthA) - 1, parseInt(dayA));
          const dateB = new Date(parseInt(yearB), parseInt(monthB) - 1, parseInt(dayB));
          return dateA.getTime() - dateB.getTime();
        });

      // Pendências: todas as transações da listagem (já filtradas para não pagas)
      const pendingTransactions = upcomingTransactions.length;

      // Calcular despesas por categoria do mês atual (pagas e a vencer)
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      const currentMonthExpenses = enrichedTransactions.filter((t: Transaction) => {
        // Normalizar data para evitar problemas de timezone
        const normalizedDueDate = normalizeDate(t.due_date);
        const [year, month, day] = normalizedDueDate.split('-');
        const transactionDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return transactionDate.getMonth() === currentMonth &&
               transactionDate.getFullYear() === currentYear &&
               t.type === 'expense';
      });


      const expensesByCategoryMap = new Map<string, { amount: number; color: string }>();
      
      // Coletar todas as categorias únicas primeiro
      const uniqueCategories = new Set<string>();
      currentMonthExpenses.forEach((t: Transaction) => {
        const categoryName = t.category?.name || 'Sem categoria';
        uniqueCategories.add(categoryName);
      });
      
      // Atribuir uma cor única da paleta para cada categoria
      const categoryColorMap = new Map<string, string>();
      let colorIndex = 0;
      uniqueCategories.forEach((categoryName) => {
        categoryColorMap.set(categoryName, COLOR_PALETTE[colorIndex % COLOR_PALETTE.length]);
        colorIndex++;
      });
      
      // Processar as transações usando as cores atribuídas
      currentMonthExpenses.forEach((t: Transaction) => {
        const categoryName = t.category?.name || 'Sem categoria';
        const categoryColor = categoryColorMap.get(categoryName)!;
        
        if (expensesByCategoryMap.has(categoryName)) {
          const existing = expensesByCategoryMap.get(categoryName)!;
          expensesByCategoryMap.set(categoryName, {
            amount: existing.amount + Math.abs(t.amount),
            color: existing.color
          });
        } else {
          expensesByCategoryMap.set(categoryName, {
            amount: Math.abs(t.amount),
            color: categoryColor
          });
        }
      });

      const expensesByCategory = Array.from(expensesByCategoryMap.entries())
        .map(([category, data]) => ({
          category,
          amount: data.amount,
          color: data.color
        }))
        .sort((a, b) => b.amount - a.amount);

      // Calcular projeções para os próximos 3 meses (todas as transações, pagas ou não)
      const futureProjections: { month: string; income: number; expenses: number }[] = [];
      const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                          'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      
      for (let i = 1; i <= 3; i++) {
        const targetDate = new Date(currentYear, currentMonth + i, 1);
        const targetMonth = targetDate.getMonth();
        const targetYear = targetDate.getFullYear();
        
        const monthTransactions = enrichedTransactions.filter((t: Transaction) => {
          // Normalizar data para evitar problemas de timezone
          const normalizedDueDate = normalizeDate(t.due_date);
          const [year, month, day] = normalizedDueDate.split('-');
          const transactionDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return transactionDate.getMonth() === targetMonth &&
                 transactionDate.getFullYear() === targetYear;
        });

        const monthIncome = monthTransactions
          .filter((t: Transaction) => t.type === 'income')
          .reduce((sum: number, t: Transaction) => sum + Math.abs(t.amount), 0);

        const monthExpenses = monthTransactions
          .filter((t: Transaction) => t.type === 'expense')
          .reduce((sum: number, t: Transaction) => sum + Math.abs(t.amount), 0);

        futureProjections.push({
          month: monthNames[targetMonth],
          income: monthIncome,
          expenses: monthExpenses
        });
      }

      // Buscar tarefas próximas de vencer (próximos 3 dias) ou urgentes e importantes
      let upcomingTasks: Task[] = [];
      try {
        const tasksResponse = await api.get('/api/v1/tasks/upcoming?days=3');
        const urgentImportantResponse = await api.get('/api/v1/tasks/urgent-important');
        
        const upcomingTasksList = tasksResponse.data || [];
        const urgentImportantList = urgentImportantResponse.data || [];
        
        // Combinar e remover duplicatas
        const allTasks = [...upcomingTasksList, ...urgentImportantList];
        const uniqueTasks = allTasks.filter((task: Task, index: number, self: Task[]) => 
          index === self.findIndex((t: Task) => t.id === task.id)
        );
        
        // Filtrar apenas pendentes e ordenar por data
        upcomingTasks = uniqueTasks
          .filter((t: Task) => t.status === 'pending')
          .sort((a: Task, b: Task) => {
            // Normalizar datas para evitar problemas de timezone
            const normalizedA = normalizeDate(a.due_date);
            const normalizedB = normalizeDate(b.due_date);
            const [yearA, monthA, dayA] = normalizedA.split('-');
            const [yearB, monthB, dayB] = normalizedB.split('-');
            const dateA = new Date(parseInt(yearA), parseInt(monthA) - 1, parseInt(dayA));
            const dateB = new Date(parseInt(yearB), parseInt(monthB) - 1, parseInt(dayB));
            return dateA.getTime() - dateB.getTime();
          });
      } catch (error) {
        console.error('Erro ao buscar tarefas:', error);
      }

      const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const previousMonthKey = `${previousMonth.getFullYear()}-${String(previousMonth.getMonth() + 1).padStart(2, '0')}`;

      const missingOkrIndicators: OkrObjective[] = await Promise.all(
        (objectives as OkrObjective[]).map(async (objective) => {
          const keyResults = objective.key_results || [];
          if (keyResults.length === 0) {
            return objective;
          }

          const indicatorsByKeyResult = await Promise.all(
            keyResults.map(async (keyResult) => {
              const indicatorsRes = await api.get(`/api/v1/key-results/${keyResult.id}/indicators`);
              return indicatorsRes.data as OkrIndicator[];
            })
          );

          const hasIndicatorLastMonth = indicatorsByKeyResult.some((indicators) =>
            indicators.some((indicator) => indicator.month === previousMonthKey)
          );

          return hasIndicatorLastMonth ? null : objective;
        })
      ).then((list) => list.filter((item): item is OkrObjective => Boolean(item)));

      setDashboardData({
        totalBalance,
        totalIncome,
        totalExpenses,
        pendingTransactions,
        upcomingTransactions,
        accounts,
        upcomingTasks,
        missingOkrIndicators,
        expensesByCategory,
        futureProjections
      });
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      // Define dados vazios para evitar tela em branco
      // O hybridApi já deve ter feito fallback para local, mas se ainda falhar, mostra dados vazios
      setDashboardData({
        totalBalance: 0,
        totalIncome: 0,
        totalExpenses: 0,
        pendingTransactions: 0,
        upcomingTransactions: [],
        accounts: [],
        upcomingTasks: [],
        missingOkrIndicators: [],
        expensesByCategory: [],
        futureProjections: []
      });
    } finally {
      setLoading(false);
    }
  }, [enrichTransactions]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Carregando...</h1>
          <p>Aguarde enquanto carregamos seus dados</p>
        </div>
      </div>
    );
  }

  const upcomingTotal = dashboardData.upcomingTransactions.reduce(
    (sum, transaction) => sum + Math.abs(transaction.amount),
    0
  );

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Bem-vindo, {user?.name}!</h1>
        <div className="dashboard-slogan">
          <span className="plumb-icon">
            <span className="plumb-line"></span>
            <span className="plumb-weight"></span>
          </span>
          <p>Equilíbrio, Alinhamento e Precisão na organização das finanças e tarefas.</p>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Cards de resumo */}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h3>Caixa</h3>
              <p className={`card-value ${dashboardData.totalBalance >= 0 ? 'positive' : 'negative'}`}>
                {isVisible ? formatCurrency(dashboardData.totalBalance) : maskCurrency(formatCurrency(dashboardData.totalBalance))}
              </p>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon">📈</div>
            <div className="card-content">
              <h3>Receitas</h3>
              <p className="card-value positive">
                {isVisible ? formatCurrency(dashboardData.totalIncome) : maskCurrency(formatCurrency(dashboardData.totalIncome))}
              </p>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon">📉</div>
            <div className="card-content">
              <h3>Despesas</h3>
              <p className="card-value negative">
                {isVisible ? formatCurrency(dashboardData.totalExpenses) : maskCurrency(formatCurrency(dashboardData.totalExpenses))}
              </p>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon">⏰</div>
            <div className="card-content">
              <h3>Pendências</h3>
              <p className="card-value">{dashboardData.pendingTransactions}</p>
            </div>
          </div>
        </div>

        {/* Gráfico de Despesas por Categoria */}
        <div className="chart-container">
          <h2>Despesas por Categoria - {new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).charAt(0).toUpperCase() + new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }).slice(1)}</h2>
          {dashboardData.expensesByCategory.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h3>Nenhuma despesa registrada este mês</h3>
              <p>Comece registrando suas despesas para ver o gráfico</p>
              <Link to="/transactions" className="primary-button">
                Adicionar Transação
              </Link>
            </div>
          ) : (
            <div className="chart-wrapper">
              <Doughnut
                data={{
                  labels: dashboardData.expensesByCategory.map(item => item.category),
                  datasets: [
                    {
                      label: 'Despesas',
                      data: dashboardData.expensesByCategory.map(item => item.amount),
                      backgroundColor: dashboardData.expensesByCategory.map((item, index) => {
                        // Garantir que cada item tenha uma cor única
                        return item.color || COLOR_PALETTE[index % COLOR_PALETTE.length];
                      }),
                      borderWidth: 2,
                      borderColor: '#fff'
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  layout: {
                    padding: 0
                  },
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const label = context.label || '';
                          const value = context.parsed || 0;
                          const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                          const percentage = ((value / total) * 100).toFixed(1);
                          return `${label}: ${formatCurrency(value)} (${percentage}%)`;
                        }
                      }
                    },
                    datalabels: {
                      display: true,
                      color: '#fff',
                      font: {
                        weight: 'bold' as const,
                        size: 11
                      },
                      padding: {
                        top: 4,
                        bottom: 4,
                        left: 6,
                        right: 6
                      },
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      borderRadius: 4,
                      formatter: (value: number, ctx: any) => {
                        const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        const label = ctx.chart.data.labels[ctx.dataIndex];
                        
                        // Mostrar nome da categoria e porcentagem se a fatia for grande o suficiente
                        if (parseFloat(percentage) >= 8) {
                          // Para fatias grandes, mostrar nome e porcentagem em duas linhas
                          return [`${label}`, `${percentage}%`];
                        } else if (parseFloat(percentage) >= 5) {
                          // Para fatias médias, mostrar apenas porcentagem
                          return `${percentage}%`;
                        }
                        // Para fatias pequenas, não mostrar nada
                        return '';
                      },
                      anchor: 'center' as const,
                      align: 'center' as const,
                      textAlign: 'center' as const
                    }
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* Gráfico de Projeção dos Próximos 3 Meses */}
        <div className="chart-container">
          <h2>Previsão - Próximos 3 Meses</h2>
          {dashboardData.futureProjections.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📈</div>
              <h3>Nenhuma transação prevista</h3>
              <p>Adicione transações futuras para ver as projeções</p>
              <Link to="/transactions" className="primary-button">
                Adicionar Transação
              </Link>
            </div>
          ) : (
            <div className="chart-wrapper">
              <Bar
                data={{
                  labels: dashboardData.futureProjections.map(item => item.month),
                  datasets: [
                    {
                      label: 'Receitas',
                      data: dashboardData.futureProjections.map(item => item.income),
                      backgroundColor: '#10b981',
                      borderRadius: 8
                    },
                    {
                      label: 'Despesas',
                      data: dashboardData.futureProjections.map(item => item.expenses),
                      backgroundColor: '#ef4444',
                      borderRadius: 8
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        padding: 15,
                        usePointStyle: true,
                        font: {
                          size: 12
                        }
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const label = context.dataset.label || '';
                          const value = context.parsed.y || 0;
                          return `${label}: ${formatCurrency(value)}`;
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function(value) {
                          return formatCurrency(Number(value));
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* Próximos vencimentos */}
        <div className="recent-transactions">
          <div className="upcoming-header">
            <h2>Próximos Vencimentos</h2>
            <span className="upcoming-total">
              {isVisible ? formatCurrency(upcomingTotal) : maskCurrency(formatCurrency(upcomingTotal))}
            </span>
          </div>
          <p className="section-subtitle">Transações pendentes vencidas ou que vencem em até 5 dias</p>
          {dashboardData.upcomingTransactions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📅</div>
              <h3>Nenhum vencimento pendente</h3>
              <p>Não há transações pendentes vencendo nos próximos 5 dias</p>
              <Link to="/transactions" className="primary-button">
                Ver Todas as Transações
              </Link>
            </div>
          ) : (
            <div className="transactions-list">
              {dashboardData.upcomingTransactions.map((transaction) => {
                // Determinar urgência baseada na proximidade do vencimento
                // Normalizar data para evitar problemas de timezone
                const normalizedDueDate = normalizeDate(transaction.due_date);
                const [year, month, day] = normalizedDueDate.split('-');
                const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                dueDate.setHours(0, 0, 0, 0);
                const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                
                let urgencyClass = 'normal';
                let urgencyLabel = '';
                if (daysUntilDue < 0) {
                  urgencyClass = 'overdue';
                  urgencyLabel = `Vencida há ${Math.abs(daysUntilDue)} dia(s)`;
                } else if (daysUntilDue === 0) {
                  urgencyClass = 'urgent';
                  urgencyLabel = 'Vence hoje';
                } else if (daysUntilDue === 1) {
                  urgencyClass = 'urgent';
                  urgencyLabel = 'Vence amanhã';
                } else if (daysUntilDue <= 3) {
                  urgencyClass = 'warning';
                  urgencyLabel = `Vence em ${daysUntilDue} dias`;
                } else {
                  urgencyClass = 'normal';
                  urgencyLabel = `Vence em ${daysUntilDue} dias`;
                }
                
                // Proteção contra dados undefined
                const category = transaction.category || { name: 'Categoria não encontrada', icon: '📋', icon_color: '#666666', color: '#666666' };
                const account = transaction.account || { name: 'Conta não encontrada' };
                
                return (
                <div key={transaction.id} className={`transaction-item ${urgencyClass}`}>
                  <div className="transaction-info">
                    <div 
                      className="category-icon" 
                      style={{ color: category.icon_color || category.color || '#666666' }}
                    >
                      {category.icon || '📋'}
                    </div>
                    <div>
                      <h4 className={transaction.is_paid ? 'paid-transaction' : ''}>
                        {transaction.description}
                        {transaction.is_recurring && (
                          <span className="recurring-icon" title="Transação recorrente">🔄</span>
                        )}
                        {transaction.installment_number && (
                          <span className="installment-badge" title={`Parcela ${transaction.installment_number}`}>
                            {transaction.installment_number}
                          </span>
                        )}
                      </h4>
                      <p className="transaction-meta">
                        {category.name} • {account.name}
                      </p>
                      <span className={`urgency-label ${urgencyClass}`}>
                        {urgencyLabel}
                      </span>
                      {transaction.is_paid && (
                        <span className="paid-badge">
                          ✅ {transaction.type === 'income' ? 'Recebido' : 'Pago'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="transaction-details">
                    <span className={`amount ${transaction.type}`}>
                      {transaction.type === 'income' ? '+' : '-'}
                      {isVisible ? formatCurrency(Math.abs(transaction.amount)) : maskCurrency(formatCurrency(Math.abs(transaction.amount)))}
                    </span>
                    <span className="transaction-date">
                      {formatDate(normalizeDate(transaction.due_date))}
                    </span>
                    <div className="transaction-actions">
                      {!transaction.is_paid ? (
                        <button 
                          className="mark-paid-button"
                          onClick={() => handleMarkAsPaid(transaction.id)}
                          title={transaction.type === 'income' ? 'Marcar como recebido' : 'Marcar como pago'}
                        >
                          💵
                        </button>
                      ) : (
                        <button 
                          className="mark-unpaid-button"
                          onClick={() => handleMarkAsUnpaid(transaction.id)}
                          title={transaction.type === 'income' ? 'Desfazer recebimento' : 'Desfazer pagamento'}
                        >
                          ↩️
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
              <div className="view-all">
                <Link to="/transactions" className="secondary-button">
                  Ver Todas as Transações
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Tarefas Próximas de Vencer */}
        <div className="recent-transactions">
          <h2>Tarefas Próximas de Vencer</h2>
          <p className="section-subtitle">Tarefas pendentes vencendo em até 3 dias ou urgentes e importantes</p>
          {dashboardData.upcomingTasks.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">✅</div>
              <h3>Nenhuma tarefa pendente</h3>
              <p>Não há tarefas vencendo nos próximos 3 dias ou urgentes e importantes</p>
              <Link to="/tasks" className="primary-button">
                Ver Todas as Tarefas
              </Link>
            </div>
          ) : (
            <div className="transactions-list">
              {dashboardData.upcomingTasks.map((task) => {
                // Normalizar data para evitar problemas de timezone
                const normalizedDueDate = normalizeDate(task.due_date);
                const [year, month, day] = normalizedDueDate.split('-');
                const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                dueDate.setHours(0, 0, 0, 0);
                const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                
                let urgencyClass = 'normal';
                let urgencyLabel = '';
                if (daysUntilDue < 0) {
                  urgencyClass = 'overdue';
                  urgencyLabel = `Vencida há ${Math.abs(daysUntilDue)} dia(s)`;
                } else if (daysUntilDue === 0) {
                  urgencyClass = 'urgent';
                  urgencyLabel = 'Vence hoje';
                } else if (daysUntilDue === 1) {
                  urgencyClass = 'urgent';
                  urgencyLabel = 'Vence amanhã';
                } else if (daysUntilDue <= 3) {
                  urgencyClass = 'warning';
                  urgencyLabel = `Vence em ${daysUntilDue} dias`;
                } else {
                  urgencyClass = 'normal';
                  urgencyLabel = `Vence em ${daysUntilDue} dias`;
                }

                return (
                  <div key={task.id} className="transaction-card">
                    <div className="transaction-info">
                      <div>
                        <h4>
                          {task.title}
                          {task.is_urgent && task.is_important && (
                            <span className="recurring-icon" title="Urgente e Importante">🔥</span>
                          )}
                        </h4>
                        {task.description && (
                          <p className="transaction-meta">{task.description}</p>
                        )}
                        <span className={`urgency-label ${urgencyClass}`}>
                          {urgencyLabel}
                        </span>
                      </div>
                    </div>
                    <div className="transaction-details">
                      <span className="transaction-date">
                        {formatDate(task.due_date)}
                      </span>
                    </div>
                  </div>
                );
              })}
              <div className="view-all">
                <Link to="/tasks" className="secondary-button">
                  Ver Todas as Tarefas
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Metas sem indicadores no mês anterior */}
        <div className="recent-transactions">
          <h2>Metas sem indicador no mês anterior</h2>
          <p className="section-subtitle">Objetivos que ficaram sem acompanhamento no último mês</p>
          {dashboardData.missingOkrIndicators.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🎯</div>
              <h3>Todas as metas foram acompanhadas</h3>
              <p>Nenhum objetivo ficou sem indicadores no mês anterior</p>
              <Link to="/objectives" className="primary-button">
                Ver Metas
              </Link>
            </div>
          ) : (
            <div className="transactions-list">
              {dashboardData.missingOkrIndicators.map((objective) => (
                <div key={objective.id} className="transaction-card">
                  <div className="transaction-info">
                    <div>
                      <h4>{objective.title}</h4>
                      <p className="transaction-meta">
                        Período: {objective.period_type === 'annual' ? 'Anual' : 'Trimestral'} •{' '}
                        {formatDate(normalizeDate(objective.period_start))} - {formatDate(normalizeDate(objective.period_end))}
                      </p>
                    </div>
                  </div>
                  <div className="transaction-details">
                    <Link to={`/objectives/${objective.id}`} className="secondary-button">
                      Ver detalhes
                    </Link>
                  </div>
                </div>
              ))}
              <div className="view-all">
                <Link to="/objectives" className="secondary-button">
                  Ver todas as metas
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;